import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DollarSign, RefreshCw, AlertTriangle, CheckCircle2, ExternalLink, Loader2,
  Ghost, UserMinus, UserPlus,
} from "lucide-react";

const fmt$ = (cents, currency = "usd") => {
  const n = (cents || 0) / 100;
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toFixed(2)} ${currency.toUpperCase()}`;
};

export default function StripeSyncReport() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.get("/admin/stripe-sync-report");
      setData(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const inSync = data && data.stripe_active_count === data.matched
    && data.ghost_pros.length === 0
    && data.unlinked_paying.length === 0
    && data.orphaned_subs.length === 0;

  return (
    <div
      data-testid="stripe-sync-report"
      className="rounded-lg border p-6"
      style={{ backgroundColor: "#fff", borderColor: "rgba(0,0,0,0.08)" }}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline" style={{ color: "var(--terracotta)" }}>Stripe reality check</div>
          <div className="font-serif text-2xl mt-1">Where&apos;s the money, actually?</div>
          <p className="text-sm mt-2 max-w-lg" style={{ color: "var(--ink-soft)" }}>
            Cross-checks every active subscription on your live Stripe account against every
            &ldquo;Pro&rdquo; user in your database. Shows your actual Stripe balance and next payout.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <a
            href="https://dashboard.stripe.com/payouts"
            target="_blank"
            rel="noreferrer"
            className="text-xs underline inline-flex items-center gap-1"
            style={{ color: "var(--ink-soft)" }}
          >
            Open Stripe payouts <ExternalLink size={11} />
          </a>
          <Button
            type="button"
            onClick={load}
            disabled={busy}
            data-testid="sync-report-run"
            className="btn-primary rounded-full inline-flex items-center gap-2"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {data ? "Refresh" : "Run report"}
          </Button>
        </div>
      </div>

      {err && (
        <div
          data-testid="sync-report-error"
          className="mt-5 text-sm rounded-md p-3"
          style={{ backgroundColor: "rgba(31,93,52,0.08)", color: "var(--terracotta)" }}
        >
          {err}
        </div>
      )}

      {data && (
        <div className="mt-8 space-y-6">
          {/* Money row */}
          <div className="grid sm:grid-cols-3 gap-4">
            <div
              className="rounded-md border p-4"
              style={{
                borderColor: "rgba(111,125,85,0.35)",
                backgroundColor: "rgba(111,125,85,0.06)",
              }}
            >
              <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--moss)" }}>
                Available balance
              </div>
              <div className="font-serif text-2xl mt-1">
                {fmt$(data.balance.available_cents, data.balance.currency)}
              </div>
              <div className="text-[11px] mt-1" style={{ color: "var(--ink-soft)" }}>
                Ready to payout to your bank
              </div>
            </div>
            <div
              className="rounded-md border p-4"
              style={{ borderColor: "rgba(196,139,46,0.35)", backgroundColor: "rgba(196,139,46,0.05)" }}
            >
              <div className="text-[10px] uppercase tracking-widest" style={{ color: "#c48b2e" }}>
                Pending balance
              </div>
              <div className="font-serif text-2xl mt-1">
                {fmt$(data.balance.pending_cents, data.balance.currency)}
              </div>
              <div className="text-[11px] mt-1" style={{ color: "var(--ink-soft)" }}>
                In Stripe&apos;s 7-day rolling hold (new merchants)
              </div>
            </div>
            <div
              className="rounded-md border p-4"
              style={{ borderColor: "rgba(0,0,0,0.1)" }}
            >
              <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
                Next payout
              </div>
              {data.next_payout ? (
                <>
                  <div className="font-serif text-2xl mt-1">
                    {fmt$(data.next_payout.amount_cents, data.next_payout.currency)}
                  </div>
                  <div className="text-[11px] mt-1" style={{ color: "var(--ink-soft)" }}>
                    {(data.next_payout.arrival_date || "").slice(0, 10)} · {data.next_payout.status}
                  </div>
                </>
              ) : (
                <div className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
                  No pending payout yet — Stripe schedules this once available balance builds up.
                </div>
              )}
            </div>
          </div>

          {/* In-sync summary */}
          <div
            className="rounded-md p-4 border flex items-start gap-3"
            style={{
              backgroundColor: inSync ? "rgba(111,125,85,0.06)" : "rgba(31,93,52,0.06)",
              borderColor: inSync ? "rgba(111,125,85,0.35)" : "rgba(31,93,52,0.35)",
            }}
          >
            {inSync ? (
              <CheckCircle2 size={18} className="mt-0.5 shrink-0" style={{ color: "var(--moss)" }} />
            ) : (
              <AlertTriangle size={18} className="mt-0.5 shrink-0" style={{ color: "var(--terracotta)" }} />
            )}
            <div className="text-sm">
              <div className="font-medium">
                Stripe: <strong>{data.stripe_active_count}</strong> active subscription{data.stripe_active_count === 1 ? "" : "s"}
                {" · "}
                AddVendTour DB: <strong>{data.db_pro_count}</strong> flagged Pro
                {" · "}
                <span style={{ color: inSync ? "var(--moss)" : "var(--terracotta)" }}>
                  {data.matched} matched
                </span>
              </div>
              <div className="mt-1 text-xs" style={{ color: "var(--ink-soft)" }}>
                {inSync
                  ? "Perfectly in sync. Every Pro is a real paying customer."
                  : `Found ${data.ghost_pros.length} ghost pro(s), ${data.unlinked_paying.length} unlinked paying user(s), ${data.orphaned_subs.length} orphaned Stripe sub(s). See below.`}
              </div>
            </div>
          </div>

          {/* Mismatch tables */}
          <div className="grid lg:grid-cols-3 gap-4">
            <MismatchList
              title="Ghost Pros"
              subtitle="Flagged Pro in DB but no active Stripe sub"
              icon={Ghost}
              items={data.ghost_pros.map((g) => ({ line1: g.email, line2: g.name || "" }))}
              tone="warn"
            />
            <MismatchList
              title="Unlinked paying"
              subtitle="Paying on Stripe but DB says free — run Reconcile"
              icon={UserPlus}
              items={data.unlinked_paying.map((u) => ({ line1: u.email, line2: `${u.interval} · ${u.subscription_id}` }))}
              tone="alert"
            />
            <MismatchList
              title="Orphaned Stripe subs"
              subtitle="Paying customer with no AddVendTour account"
              icon={UserMinus}
              items={data.orphaned_subs.map((u) => ({ line1: u.email, line2: `${u.interval} · ${u.subscription_id}` }))}
              tone="neutral"
            />
          </div>

          <div className="text-[11px]" style={{ color: "var(--ink-soft)" }}>
            <DollarSign size={11} className="inline mr-1" />
            <strong>Not seeing money in your bank?</strong> Available balance pays out on your Stripe payout schedule.
            New Stripe accounts have a 7–14 day rolling hold. Confirm your bank + payout schedule at{" "}
            <a href="https://dashboard.stripe.com/settings/payouts" target="_blank" rel="noreferrer" className="underline">
              dashboard.stripe.com/settings/payouts
            </a>.
          </div>
        </div>
      )}
    </div>
  );
}

function MismatchList({ title, subtitle, icon: Icon, items, tone }) {
  const color = tone === "alert" ? "var(--terracotta)" : tone === "warn" ? "#c48b2e" : "var(--ink-soft)";
  const bg = tone === "alert" ? "rgba(31,93,52,0.05)" : tone === "warn" ? "rgba(196,139,46,0.05)" : "rgba(0,0,0,0.03)";
  return (
    <div
      className="rounded-md border p-4"
      style={{
        borderColor: "rgba(0,0,0,0.08)",
        backgroundColor: items.length > 0 ? bg : "#fff",
      }}
    >
      <div className="flex items-center gap-2">
        <Icon size={14} style={{ color }} />
        <div className="text-sm font-medium">{title}</div>
        <span
          className="text-[10px] font-mono px-1.5 py-0.5 rounded ml-auto"
          style={{ backgroundColor: "rgba(0,0,0,0.08)", color: "var(--ink-soft)" }}
        >
          {items.length}
        </span>
      </div>
      <div className="text-[11px] mt-1" style={{ color: "var(--ink-soft)" }}>{subtitle}</div>
      {items.length === 0 ? (
        <div className="mt-3 text-xs" style={{ color: "var(--ink-soft)" }}>None. 🎉</div>
      ) : (
        <ul className="mt-3 space-y-2 text-xs">
          {items.slice(0, 12).map((it, i) => (
            <li key={i} className="min-w-0">
              <div className="truncate font-mono">{it.line1}</div>
              {it.line2 && <div className="text-[10px] truncate" style={{ color: "var(--ink-soft)" }}>{it.line2}</div>}
            </li>
          ))}
          {items.length > 12 && (
            <li className="text-[10px]" style={{ color: "var(--ink-soft)" }}>+{items.length - 12} more</li>
          )}
        </ul>
      )}
    </div>
  );
}
